/**
 * useTranscriptApplogs — Reactive transcript segment query hook
 *
 * Queries a note3 thread's applogs to discover and track transcript segments
 * for a given media ID. Uses Note3SegmentVM (based on createSolidViewModel)
 * for per-attribute reactive access — each attribute is tracked independently
 * via liveEntityAt, unlike the old per-entity LWW accumulator that could drop
 * segment/startMs when it had a lower timestamp.
 *
 * Segments are only included once all required data (transcript, startMs, endMs)
 * is present, ensuring correct ordering and display.
 */

import type { Accessor } from 'solid-js'
import type { TranscriptSegment } from './types'
import { Note3SegmentVM } from './Note3SegmentVM'
import { Logger } from 'besonders-logger'
import { createMemo } from 'solid-js'
import { useRollingAcc } from '@wovin/hooks-solid'
import { isInitEvent } from '@wovin/core'
import { useRawThread } from '../../../ui/reactive'

const { DEBUG } = Logger.setup(Logger.INFO, { prefix: '[txApplogs]' })

/** Parse diarization JSON or return raw string as label */
function parseSpeakerLabel(diarization: any): string | null {
  if (!diarization) return null
  if (typeof diarization === 'string') {
    try {
      const parsed = JSON.parse(diarization)
      return parsed.label || diarization
    } catch {
      return diarization
    }
  }
  return diarization.label || String(diarization)
}

function parseFloatOrNull(val: any): number | null {
  if (val == null) return null
  const n = Number(val)
  return Number.isFinite(n) ? n : null
}

/**
 * Hook that subscribes to segment applogs for a given mediaId accessor.
 *
 * Uses a lightweight rollingAcc to maintain a reactive Set of discovered
 * segment entity IDs, then delegates attribute access to Note3SegmentVM
 * (which provides correct per-attribute reactive tracking).
 */
export function useTranscriptApplogs(mediaIdAccessor: Accessor<string | undefined>) {
  const rawThread = useRawThread()

  // ── Lightweight segment ID discovery ────────────────────────────
  // We only track IDs here — attribute access is handled by Note3SegmentVM.
  const segmentIds = useRollingAcc<Set<string>>(
    new Set(),
    (event, set) => {
      const mediaId = mediaIdAccessor()
      if (!mediaId) return

      if (!isInitEvent(event)) {
        // Process removals: if segment/media gets removed, take the ID out
        for (const removed of event.removed ?? []) {
          if (removed.at === 'segment/media') {
            // Only remove if the media matches (or we don't track by media here)
            set.delete(removed.en as string)
          }
        }
      }

      // Process additions
      const addedLogs = isInitEvent(event) ? event.init : event.added
      for (const added of addedLogs) {
        if (added.at === 'segment/media' && added.vl === mediaId) {
          set.add(added.en as string)
        }
      }
    },
    { thread: rawThread, name: 'useTranscriptApplogs.ids' },
  )

  // ── Derive sorted TranscriptSegment array ──────────────────────
  const segments = createMemo((): TranscriptSegment[] => {
    const ids = segmentIds()
    const result: TranscriptSegment[] = []

    for (const segId of ids) {
      const vm = Note3SegmentVM.get(segId, rawThread)

      // Skip deleted segments
      if ((vm as any).isDeleted === 'true') continue

      const text = (vm as any).transcript as string | undefined
      if (!text) continue

      // ── Wait for all required timing data before including —────
      // This prevents segments from appearing with startMs=0 (which wrecks
      // ordering) before the segment/startMs and segment/endMs applogs arrive.
      const startMsStr = (vm as any).startMs as string | undefined
      const endMsStr = (vm as any).endMs as string | undefined
      if (startMsStr == null || endMsStr == null) continue

      const startMs = Number(startMsStr)
      const endMs = Number(endMsStr)
      if (!Number.isFinite(startMs) || !Number.isFinite(endMs)) continue

      result.push({
        segmentId: segId,
        text,
        speakerLabel: parseSpeakerLabel((vm as any).diarization),
        speakerId: (vm as any).speakerId || null,
        speakerConfidence: parseFloatOrNull((vm as any).speakerConfidence),
        speakerSource: (vm as any).speakerSource || null,
        startMs,
        endMs,
        channelDominanceHint: (vm as any).channelDominance || null,
        isDeleted: (vm as any).isDeleted === 'true',
        isEdited: (vm as any).isEdited === 'true',
      })
    }

    // Sort by startMs ascending (primary), then endMs, then segmentId
    result.sort((a, b) =>
      a.startMs - b.startMs || a.endMs - b.endMs || a.segmentId.localeCompare(b.segmentId),
    )

    // ── Skip segments swallowed by the previous one ───────────────
    // Only when both have a non-null matching speaker label.
    const deduped: TranscriptSegment[] = []
    for (const seg of result) {
      const prev = deduped[deduped.length - 1]
      const sameSpeaker = !!prev?.speakerLabel && prev.speakerLabel === seg.speakerLabel
      if (prev && sameSpeaker && seg.startMs >= prev.startMs && seg.endMs <= prev.endMs) continue
      deduped.push(seg)
    }
    return deduped
  })

  // ── Derive unique speaker labels ────────────────────────────────
  const uniqueSpeakers = createMemo(() => {
    const labels = new Set<string>()
    for (const s of segments()) {
      if (s.speakerLabel) labels.add(s.speakerLabel)
    }
    return Array.from(labels).sort()
  })

  return { segments, uniqueSpeakers }
}
